import { MAIN_ROUTES } from 'routes';
import LeftNavigationBar from './components/LeftNavigationBar';
import TopNavigationBar from './components/TopNavigationBar';
import UnverifiedEmailBanner from 'components/UnverifiedEmailBanner/UnverifiedEmailBanner';
import UnverifiedPhoneBanner from 'components/UnverifiedPhoneBanner/UnverifiedPhoneBanner';
import { ImpersonationBanner } from 'components/ImpersonationBanner';
import MaintenanceBanner from 'components/MaintenanceBanner/MaintenanceBanner';
import LoginPromptModal from 'components/LoginPromptModal/LoginPromptModal';
import Footer from 'components/Footer';
import './Main.css';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useGetMeQuery } from 'store/services/user/meApi';

interface Props {
  currentTab: (typeof MAIN_ROUTES)[number];
  backgroundImage?: string; // Optional blur background
}

export const Main = ({ currentTab, backgroundImage }: Props) => {
  const { data: userData } = useGetMeQuery();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMobile || !isMobileMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Ignore clicks on the hamburger button (let its onClick handler manage the toggle)
      if (target.closest('.mobile-hamburger')) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(target)) {
        setIsMobileMenuOpen(false);
      }
    };

    // Add listener with a slight delay to avoid closing immediately after opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="App">
      <div className="main">
        {/* Sidebar */}
        <LeftNavigationBar
          ref={menuRef}
          currentTab={currentTab}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
          isPublicMode={!currentUser}
          onNavigationAttempt={() => setShowLoginModal(true)}
          isAuthenticated={!!currentUser}
        />

        {/* Main content area */}
        <div className="content">
          {/* Header stays above blur */}
          <TopNavigationBar
            title={currentTab.title ?? ''}
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />

          {/* Impersonation banner - shows when admin is impersonating a user */}
          {localStorage.getItem('isImpersonating') === 'true' && (
            <ImpersonationBanner
              impersonatedUsername={localStorage.getItem('impersonatedUsername') || ''}
              targetUserId={parseInt(localStorage.getItem('impersonatedUserId') || '0', 10)}
            />
          )}

          {/* Maintenance mode banner - shows when maintenance mode is active and user is admin */}
          <MaintenanceBanner />

          {/* Email verification banner - Priority 1 (hidden on home page to reduce clutter with onboarding checklist) */}
          {currentTab.path !== '/home' && <UnverifiedEmailBanner />}

          {/* Phone verification banner - Priority 2 (only shows if email is verified, hidden on home page) */}
          {currentTab.path !== '/home' && userData?.emailVerified && <UnverifiedPhoneBanner />}

          {/* Content with optional background blur */}
          <div className="main-content-wrapper">
            {backgroundImage && (
              <div
                className="main-blur-bg"
                style={{ backgroundImage: `url('${backgroundImage}')` }}
              />
            )}

            <div className="main-page-content">{currentTab.Component}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Login prompt modal for unauthenticated users */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername=""
      />
    </div>
  );
};
